import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, X, ArrowRight, Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import HowItWorks from '../components/home/HowItWorks';
import { serviceProducts, getProductImage } from '../data/serviceProducts';
import { mainServiceGroups, getMainService, serviceName } from '../data/mainServices';
import { getServiceTypeImage } from '../data/navServices';
import {
  PARENT_BY_CATEGORY,
  filterTypesFor,
  TRANSLATION_SLUGS,
  DOCUMENT_SLUGS,
} from '../data/serviceFilters';
import { publicApi } from '../api/client';
import ServicePriceRows from '../components/common/ServicePriceRows';
import assetUrl from '../assetUrl';
import './AllServicesPage.css';

const PAGE_SIZE = 12;

// 2 filtres : « Type de traduction » (professionnelle + officielle) et « Type de document »
const FILTER_GROUPS = [
  {
    groupId: 'translationType',
    allLabel: 'Toutes',
    types: mainServiceGroups
      .filter((g) => g.id !== 'documents')
      .flatMap((g) => g.services.map((s) => s.slug)),
  },
  {
    groupId: 'documentType',
    allLabel: 'Tous',
    types: (mainServiceGroups.find((g) => g.id === 'documents')?.services || []).map((s) => s.slug),
    other: true,
  },
];

// Slugs couverts par les filtres — le reste tombe sous « Autre (etc.) »

// Slugs couverts par les filtres — le reste tombe sous « Autre (etc.) »
const ALL_FILTER_SLUGS = new Set([...TRANSLATION_SLUGS, ...DOCUMENT_SLUGS]);

// Liste locale de secours (même liste, mais SANS prix ni avis — données dynamiques non fiables hors-ligne)
const FALLBACK_PRODUCTS = serviceProducts
  .filter((p) => p.id !== 'onlineInterpreter')
  .map((p) => ({
    ...p,
    filterType: PARENT_BY_CATEGORY[p.category] || 'general',
    filterTypes: filterTypesFor(p),
    image: getProductImage(p),
    price: 0,
    priceMax: 0,
    rating: 0,
    orders: 0,
  }));

// Noms du services.json (fr/en/ar) prioritaires ; i18n en fallback
function getTypeLabel(typeId, t, lang) {
  const jsonName = serviceName(getMainService(typeId), lang);
  return jsonName
    || t.nav?.servicesMenu?.[typeId]
    || t.serviceTypes?.types?.[typeId]?.title
    || typeId;
}

function getGroupLabel(groupId, t) {
  if (groupId === 'translationType') return 'Type de traduction';
  if (groupId === 'documentType') return 'Type de document';
  return t.nav?.servicesMenu?.[`${groupId}Group`] || groupId;
}

function ServiceCard({ product, t, locale, lang }) {
  const copy = t.serviceCatalog.products[product.id];
  const hasPrice = (product.price || 0) > 0
    || Object.values(product.prices || {}).some((v) => Number(v) > 0)
    || (product.typePricing || []).some((e) => Number(e.price) > 0);
  const image = product.image || getProductImage(product);
  const reviewCount = product.reviewsCount || 0;
  const reviewLabel = reviewCount.toLocaleString(locale);
  const title = product.dbTitle || copy?.title || product.id;
  const displayTitle = lang === 'ar' ? title : title.toUpperCase();

  return (
    <div className="all-services-card">
      <div className="all-services-card-media">
        <img
          src={assetUrl(`${image}?v=12`)}
          alt=""
          loading="lazy"
          decoding="async"
        />
        <Link to="/order" className="asc-order" aria-label="Commander en ligne">
          <span>Commander</span>
        </Link>
      </div>
      <div className="all-services-card-body">
        <h3>{displayTitle}</h3>
        <div className="all-services-card-rating">
          <div className="all-services-card-stars" aria-label={`${reviewLabel} ${t.serviceCatalog.reviews}`}>
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                size={14}
                fill={reviewCount > 0 ? 'currentColor' : 'none'}
                aria-hidden="true"
              />
            ))}
          </div>
          <span className="all-services-card-reviews">
            {reviewLabel} {t.serviceCatalog.reviews}
          </span>
        </div>
        {hasPrice && <ServicePriceRows service={product} locale={locale} />}
      </div>
    </div>
  );
}

export default function AllServicesPage({ initialProducts = null }) {
  const { t, lang, isRTL } = useLanguage();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeType, setActiveType] = useState('all');
  const [page, setPage] = useState(1);

  // Source principale = base de données ; secours local (sans prix/avis) si l'API est coupée
  const [dbServices, setDbServices] = useState(null);
  useEffect(() => {
    let alive = true;
    publicApi
      .subServices('')
      .then((res) => { if (alive) setDbServices(res.subServices || []); })
      .catch(() => { if (alive) setDbServices([]); });
    return () => { alive = false; };
  }, []);

  const products = useMemo(() => {
    if (initialProducts) {
      return initialProducts.map((p) => ({
        ...p,
        filterType: p.filterType || PARENT_BY_CATEGORY[p.category] || p.type,
        filterTypes: filterTypesFor(p),
        image: p.image || getProductImage(p),
      }));
    }
    if (!dbServices || dbServices.length === 0) return FALLBACK_PRODUCTS;
    return dbServices.filter((s) => s.type !== 'interpreting' && s.category !== 'interpreting').map((s) => ({
      id: s.slug,
      slug: s.slug,
      filterType: s.type,
      filterTypes: filterTypesFor(s),
      dbTitle: (lang === 'ar' ? s.titleAr : lang === 'en' ? s.titleEn : s.titleFr) || s.titleFr,
      image: s.image || getServiceTypeImage(s.type),
      price: s.basePrice || 0,
      priceMax: s.priceMax || 0,
      unit: s.unit || '',
      prices: s.prices || {},
      priceEur: s.priceEur || 0,
      typePricing: s.typePricing || [],
      rating: s.rating || 0,
      orders: s.orders || 0,
      reviewsCount: s.reviewsCount || 0,
    }));
  }, [initialProducts, dbServices, lang]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, activeType]);

  const locale = lang === 'ar' ? 'ar-TN' : lang === 'en' ? 'en-GB' : 'fr-FR';

  const filtered = useMemo(() => {
    let list;
    if (activeType === 'all') {
      list = [...products];
    } else if (activeType === 'other') {
      list = products.filter((p) => !(p.filterTypes || [p.filterType]).some((f) => ALL_FILTER_SLUGS.has(f)));
    } else {
      list = products.filter((p) => (p.filterTypes || [p.filterType]).includes((activeType || '').toLowerCase()));
    }
    const normalizedQuery = debouncedQuery.toLowerCase();
    if (normalizedQuery) {
      list = list.filter((p) => {
        const copy = t.serviceCatalog.products[p.id];
        const haystack = `${p.dbTitle || ''} ${copy?.title || ''} ${copy?.short || ''}`.toLowerCase();
        return haystack.includes(normalizedQuery);
      });
    }
    list.sort((a, b) => (b.orders || 0) - (a.orders || 0) || (b.rating || 0) - (a.rating || 0));
    return list;
  }, [activeType, debouncedQuery, t.serviceCatalog, products]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE) || 1;
  const shown = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const canGoBack = page > 1;
  const canGoNext = page < totalPages;

  return (
    <div className={`all-services-page ${isRTL ? 'rtl' : ''}`}>
      <header
        className="all-services-hero"
        style={{
          backgroundImage:
            `linear-gradient(135deg, rgba(12, 30, 60, 0.92) 0%, rgba(21, 42, 74, 0.88) 60%, rgba(26, 122, 122, 0.78) 100%), ` +
            `url(${assetUrl('/images/hero-sworn-translation.jpg')})`,
        }}
      >
        <div className="all-services-hero-glow" aria-hidden="true" />
        <div className="container all-services-hero-inner">
          <h1>{t.serviceCatalog.allServicesTitle}</h1>
          <p>{t.serviceCatalog.allServicesLead}</p>
          <Link to="/order" className="btn btn-primary">
            {t.serviceCatalog.allServicesOrderCta}
            <ArrowRight size={18} aria-hidden="true" />
          </Link>
        </div>
      </header>

      <section className="all-services-filter-bar">
        <div className="container">
          <div className="all-services-filter-bar-top">
            <span className="all-services-filter-bar-label">
              {t.serviceCatalog.categoriesLabel}
            </span>
            {(query || activeType !== 'all') && (
              <button
                type="button"
                className="all-services-reset-btn"
                onClick={() => {
                  setQuery('');
                  setActiveType('all');
                }}
              >
                {t.serviceCatalog.resetFilters}
              </button>
            )}
          </div>

          <div className="all-services-search">
            <Search size={18} aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t.serviceCatalog.searchPlaceholder}
              aria-label={t.serviceCatalog.searchPlaceholder}
            />
            {query && (
              <button
                type="button"
                className="all-services-search-clear"
                onClick={() => setQuery('')}
                aria-label={t.serviceCatalog.clearSearch}
              >
                <X size={16} />
              </button>
            )}
          </div>

          <div
            className="all-services-filters"
            aria-label={t.serviceCatalog.categoriesLabel}
          >
            <div className="all-services-selects">
              {FILTER_GROUPS.map((group) => {
                const groupLabel = getGroupLabel(group.groupId, t);
                const selectId = `service-filter-${group.groupId}`;
                const value = group.types.includes(activeType)
                  || (group.other && activeType === 'other') ? activeType : '';
                const sortLocale = lang === 'ar' ? 'ar' : lang === 'en' ? 'en' : 'fr';
                const sortedTypes = [...group.types].sort((a, b) =>
                  getTypeLabel(a, t, lang).localeCompare(getTypeLabel(b, t, lang), sortLocale)
                );
                return (
                  <div key={group.groupId} className="all-services-select-group">
                    <label htmlFor={selectId} className="all-services-select-label">
                      {groupLabel}
                    </label>
                    <select
                      id={selectId}
                      className="all-services-select"
                      value={value}
                      onChange={(e) => setActiveType(e.target.value || 'all')}
                    >
                      <option value="">{group.allLabel || t.serviceCatalog.allCategories}</option>
                      {sortedTypes.map((typeId) => (
                        <option key={typeId} value={typeId}>
                          {getTypeLabel(typeId, t, lang)}
                        </option>
                      ))}
                      {group.other && <option value="other">Autre (etc.)</option>}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="all-services-catalog">
        <div className="container">
          <div className="all-services-catalog-header">
            <span className="all-services-count-badge">
              <strong>{filtered.length}</strong> {t.serviceCatalog.productCount}
            </span>
          </div>

          {dbServices === null && !initialProducts ? (
            <div className="all-services-empty">
              <div className="all-services-empty-icon">
                <Search size={28} />
              </div>
              <h3>Chargement des services…</h3>
            </div>
          ) : filtered.length === 0 ? (
            <div className="all-services-empty">
              <div className="all-services-empty-icon">
                <Search size={28} />
              </div>
              <h3>{t.serviceCatalog.noResults}</h3>
              <p>{t.serviceCatalog.noResultsHint}</p>
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  setQuery('');
                  setActiveType('all');
                }}
              >
                {t.serviceCatalog.resetFilters}
              </button>
            </div>
          ) : (
            <>
              <div className="all-services-grid">
                {shown.map((product) => (
                  <ServiceCard
                    key={product.id}
                    product={product}
                    t={t}
                    locale={locale}
                    lang={lang}
                  />
                ))}
              </div>
              {totalPages > 1 && (
                <nav className="all-services-pagination" aria-label="Pagination">
                  <button
                    type="button"
                    className="all-services-page-btn all-services-page-btn--nav"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={!canGoBack}
                  >
                    {t.order.prev}
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`all-services-page-btn ${p === page ? 'is-active' : ''}`}
                      onClick={() => setPage(p)}
                      aria-current={p === page ? 'page' : undefined}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="all-services-page-btn all-services-page-btn--nav"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={!canGoNext}
                  >
                    {t.order.next}
                  </button>
                </nav>
              )}
            </>
          )}
        </div>
      </section>

      <HowItWorks showHeader showCta={false} />

      <section className="all-services-cta-banner">
        <div className="container">
          <h2>{t.about.ctaTitle}</h2>
          <p>{t.about.ctaText}</p>
          <div className="all-services-cta-actions">
            <Link to="/order" className="btn btn-primary">
              {t.about.orderButton}
            </Link>
            <Link to="/contact" className="btn btn-outline">
              {t.about.contactButton}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
